import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notesApi, type AccountAnchor, type NotePayload } from '../lib/api'

function notesQueryKey(anchor: AccountAnchor | undefined, page = 1) {
  return [anchor?.kind === 'merchant' ? 'merchant-notes' : 'lead-notes', anchor?.id, page]
}

function invalidateNoteFamilies(queryClient: ReturnType<typeof useQueryClient>, anchor?: AccountAnchor) {
  if (anchor) {
    queryClient.invalidateQueries({ queryKey: notesQueryKey(anchor) })
  }
  queryClient.invalidateQueries({ queryKey: ['lead-notes'] })
  queryClient.invalidateQueries({ queryKey: ['merchant-notes'] })
}

export function useNotes(anchor: AccountAnchor | undefined, page: number) {
  return useQuery({
    queryKey: notesQueryKey(anchor, page),
    queryFn: () => {
      if (!anchor) throw new Error('Note anchor is required')
      return notesApi.list(anchor, page)
    },
    enabled: !!anchor?.id,
  })
}

export function useCreateNote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ anchor, payload }: { anchor: AccountAnchor; payload: NotePayload }) =>
      notesApi.create(anchor, payload),
    onSuccess: (_data, { anchor }) => {
      invalidateNoteFamilies(queryClient, anchor)
    },
  })
}

export function useDeleteNote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ noteId }: { noteId: string; anchor: AccountAnchor }) => notesApi.delete(noteId),
    onSuccess: (_data, { anchor }) => {
      invalidateNoteFamilies(queryClient, anchor)
    },
  })
}
